import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useBackHref } from '@/hooks/useListLocation';
import { graphql } from '@/gql';
import type {
  VoucherDiscountType,
  VoucherRuleTrigger,
  VoucherValidityMode,
} from '@/gql/graphql';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { useSession } from '@/features/session/useSession';
import { TicketClaimedRuleFields } from './TicketClaimedRuleFields';
import { voucherRuleErrorMessage } from './ruleErrors';
import { validityModeNeedsEndDate, type RuleNight } from './ticketClaimedRule';

const VoucherRuleDoc = graphql(/* GraphQL */ `
  query VoucherRule($id: UUID!) {
    voucherRule(id: $id) {
      id
      name
      trigger
      thresholdPoints
      validityDays
      discountType
      discountPercentage
      discountAmount
      productId
      productName
      label
      promotionLabel
      active
      nightId
      night {
        id
        title
        startsAt
        scope {
          id
          name
        }
        stages {
          id
          name
          isActive
        }
      }
      stageIds
      validityMode
      validUntil
    }
  }
`);

const ProductsForVoucherRuleDoc = graphql(/* GraphQL */ `
  query ProductsForVoucherRule {
    products(includeInactive: false, page: 1, pageSize: 200) {
      nodes {
        id
        name
      }
    }
  }
`);

const CreateVoucherRuleDoc = graphql(/* GraphQL */ `
  mutation CreateVoucherRule($input: CreateVoucherRuleInput!) {
    createVoucherRule(input: $input) {
      id
    }
  }
`);

const UpdateVoucherRuleDoc = graphql(/* GraphQL */ `
  mutation UpdateVoucherRule($id: UUID!, $input: UpdateVoucherRuleInput!) {
    updateVoucherRule(id: $id, input: $input) {
      id
      active
    }
  }
`);

export function VoucherRuleEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // The rules list as it was left (show, trigger): Cancel returns to it.
  const backHref = useBackHref('/voucher-rules');
  const isNew = !id || id === 'new';
  const { me } = useSession();

  const { data, loading, error, refetch } = useQuery(VoucherRuleDoc, {
    variables: { id: id ?? '' },
    skip: isNew,
    errorPolicy: 'all',
  });
  const productsQuery = useQuery(ProductsForVoucherRuleDoc, { errorPolicy: 'all' });
  const rule = data?.voucherRule;

  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState<VoucherRuleTrigger>('POINTS_THRESHOLD');
  const [thresholdPoints, setThresholdPoints] = useState('');
  const [validityDays, setValidityDays] = useState('30');
  const [discountType, setDiscountType] = useState<VoucherDiscountType>('PERCENTAGE');
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [productId, setProductId] = useState('');
  const [label, setLabel] = useState('');
  const [promotionLabel, setPromotionLabel] = useState('');
  const [active, setActive] = useState(true);
  const [night, setNight] = useState<RuleNight | null>(null);
  const [stageIds, setStageIds] = useState<string[]>([]);
  const [validityMode, setValidityMode] = useState<VoucherValidityMode | ''>('');
  const [validUntil, setValidUntil] = useState('');
  // The admin changed the night or the stage ticks (or is replacing a hidden night).
  const [selectionDirty, setSelectionDirty] = useState(false);
  const [replacingHidden, setReplacingHidden] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (rule && rule.id !== seededFor) {
    setSeededFor(rule.id);
    setName(rule.name);
    setTrigger(rule.trigger);
    setThresholdPoints(rule.thresholdPoints != null ? String(rule.thresholdPoints) : '');
    setValidityDays(String(rule.validityDays));
    setDiscountType(rule.discountType);
    setDiscountPercentage(
      rule.discountPercentage != null ? String(rule.discountPercentage) : '',
    );
    setDiscountAmount(rule.discountAmount != null ? String(rule.discountAmount) : '');
    setProductId(rule.productId ?? '');
    setLabel(rule.label ?? '');
    setPromotionLabel(rule.promotionLabel ?? '');
    setActive(rule.active);
    setNight(rule.night ?? null);
    setStageIds(rule.stageIds);
    setValidityMode(rule.validityMode ?? '');
    setValidUntil(rule.validUntil ?? '');
    setSelectionDirty(false);
    setReplacingHidden(false);
  }

  // A ticket-claimed rule whose night the viewer may not read: Go returns
  // nightId but a null night. Shown read-only; saving keeps it.
  const hiddenNight =
    !!rule &&
    rule.trigger === 'TICKET_CLAIMED' &&
    !!rule.nightId &&
    !rule.night &&
    !night &&
    !replacingHidden;

  const [createRule, { loading: creating }] = useMutation(CreateVoucherRuleDoc);
  const [updateRule, { loading: updating }] = useMutation(UpdateVoucherRuleDoc);

  const validate = (): string | null => {
    if (!name.trim()) return 'Name is required.';
    if (trigger === 'POINTS_THRESHOLD') {
      const n = Number.parseInt(thresholdPoints, 10);
      if (!Number.isFinite(n) || n <= 0) {
        return 'A points-threshold rule needs a positive threshold.';
      }
    } else if (thresholdPoints.trim() !== '') {
      return 'Only a points-threshold rule may have a threshold. Clear it for the other triggers.';
    }
    if (trigger === 'TICKET_CLAIMED') {
      if (!night && !hiddenNight) return 'Pick the night this rule rewards.';
      if (!validityMode) return 'Choose a validity mode.';
      if (validityModeNeedsEndDate(validityMode) && !/^\d{4}-\d{2}-\d{2}$/.test(validUntil)) {
        return 'Pick the last day the voucher is valid (Valid until).';
      }
    }
    if (discountType === 'PERCENTAGE') {
      const n = Number.parseFloat(discountPercentage);
      if (!Number.isFinite(n) || n <= 0) return 'Percentage benefit needs a whole percent.';
    }
    if (discountType === 'AMOUNT') {
      const n = Number.parseFloat(discountAmount);
      if (!Number.isFinite(n) || n <= 0) return 'Fixed-amount benefit needs a euro amount.';
    }
    if (discountType === 'FREE_PRODUCT' && !productId) {
      return 'Free-product benefit needs a catalog product.';
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const clientError = validate();
    if (clientError) {
      setFormError(clientError);
      return;
    }
    setFormError(null);
    // Go checks ticket:read whenever nightId is present, so a rename or
    // benefit change must not carry it. Send it for a new rule, when the trigger
    // becomes Ticket claimed, or when the admin touched the night or stages.
    const sendsNight =
      trigger === 'TICKET_CLAIMED' &&
      (isNew || rule?.trigger !== 'TICKET_CLAIMED' || selectionDirty);
    let nightPayload: { nightId?: string | null; stageIds?: string[] | null };
    if (trigger !== 'TICKET_CLAIMED') {
      // Other triggers carry no night; Go clears the ticket fields.
      nightPayload = { nightId: null, stageIds: null };
    } else if (sendsNight) {
      nightPayload = { nightId: night?.id ?? null, stageIds };
    } else {
      // Omitting nightId/stageIds keeps Go from running the ticket:read
      // check on edits that do not touch the target (the rule keeps its night).
      nightPayload = {};
    }
    const input = {
      name: name.trim(),
      trigger,
      thresholdPoints:
        trigger === 'POINTS_THRESHOLD' ? Number.parseInt(thresholdPoints, 10) : null,
      validityDays: Number.parseInt(validityDays, 10) || 30,
      discountType,
      discountPercentage:
        discountType === 'PERCENTAGE' ? Number.parseFloat(discountPercentage) : null,
      discountAmount: discountType === 'AMOUNT' ? Number.parseFloat(discountAmount) : null,
      productId: discountType === 'FREE_PRODUCT' ? productId : null,
      // Blank clears (Go: null leaves it alone, blank clears), so the trimmed value is always sent.
      label: label.trim(),
      // Only a Ticket claimed rule has a promotion; Go clears it on every other trigger.
      promotionLabel: trigger === 'TICKET_CLAIMED' ? promotionLabel.trim() : null,
      active,
      ...nightPayload,
      validityMode: trigger === 'TICKET_CLAIMED' ? validityMode || null : null,
      validUntil:
        trigger === 'TICKET_CLAIMED' && validityModeNeedsEndDate(validityMode)
          ? validUntil
          : null,
    };
    try {
      if (isNew) {
        const result = await createRule({ variables: { input } });
        const newId = result.data?.createVoucherRule.id;
        if (newId) navigate(`/voucher-rules/${newId}`, { replace: true, state: location.state });
      } else if (id) {
        await updateRule({ variables: { id, input } });
        await refetch();
      }
    } catch (err) {
      setFormError(voucherRuleErrorMessage(err, 'save'));
    }
  };

  if (!isNew && loading && !rule && !error) {
    return <p className="text-sm text-gray-500">Loading rule…</p>;
  }
  if (!isNew && error && !rule) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Voucher rule
        </h1>
        <QueryErrorNotice error={error} />
      </div>
    );
  }

  return (
    <FormLayout
      title={isNew ? 'New voucher rule' : `Edit ${rule?.name ?? 'rule'}`}
      description="Vouchers are minted when this rule fires — they are not bought, and the points balance is not debited."
      onSubmit={handleSubmit}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {formError ? <span className="text-sm text-red-600">{formError}</span> : null}
          <Button variant="ghost" type="button" onClick={() => navigate(backHref)}>
            Cancel
          </Button>
          <Button type="submit" loading={creating || updating}>
            {isNew ? 'Create rule' : 'Save rule'}
          </Button>
        </div>
      }
    >
      <FormSection title="Trigger">
        <FormField
          label="Name"
          required
          description="Internal name. Shown on the register chip after a voucher scan."
        >
          <Input
            id="admin.vouchers.ruleName"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </FormField>
        <FormField
          label="When this mints"
          required
          description="Threshold remints whenever they earn enough again without a live voucher. First card linked fires once ever, only after a terminal QR claim. Birthday fires once per year per configured scope. Ticket claimed mints when a ticket of one night is claimed, and that night may run in another venue."
        >
          <Select
            id="admin.vouchers.ruleTrigger"
            value={trigger}
            onChange={(e) => {
              const next = e.target.value as VoucherRuleTrigger;
              setTrigger(next);
              if (next !== 'POINTS_THRESHOLD') setThresholdPoints('');
              setFormError(null);
            }}
            options={[
              { value: 'POINTS_THRESHOLD', label: 'Points threshold' },
              { value: 'FIRST_CARD_LINKED', label: 'First card linked' },
              { value: 'BIRTHDAY', label: 'Birthday' },
              { value: 'TICKET_CLAIMED', label: 'Ticket claimed' },
            ]}
          />
        </FormField>
        {trigger === 'POINTS_THRESHOLD' ? (
          <FormField
            label="Threshold (points)"
            required
            description="Required for a threshold rule. The guest's global balance is compared; the voucher is minted in this scope."
          >
            <Input
              id="admin.vouchers.ruleThreshold"
              type="number"
              min={1}
              value={thresholdPoints}
              onChange={(e) => setThresholdPoints(e.target.value)}
              required
            />
          </FormField>
        ) : (
          <p className="text-sm text-gray-500">
            This trigger has no threshold. The server rejects a threshold on
            every trigger except points threshold.
          </p>
        )}
        {trigger === 'TICKET_CLAIMED' ? (
          <TicketClaimedRuleFields
            night={night}
            onNightChange={(next) => {
              setNight(next);
              setSelectionDirty(true);
            }}
            stageIds={stageIds}
            onStageIdsChange={(next) => {
              setStageIds(next);
              setSelectionDirty(true);
            }}
            hiddenNight={hiddenNight}
            onReplaceHidden={() => setReplacingHidden(true)}
            validityMode={validityMode}
            onValidityModeChange={setValidityMode}
            validUntil={validUntil}
            onValidUntilChange={setValidUntil}
            activeScopeName={me?.currentScope?.name}
          />
        ) : (
          <FormField label="Validity (days)" required>
            <Input
              type="number"
              min={1}
              value={validityDays}
              onChange={(e) => setValidityDays(e.target.value)}
              required
            />
          </FormField>
        )}
        <FormField
          label="Label"
          description="Shown to guests in the app, the voucher mail and the ticket mail / ticket pages. Leave empty to show no voucher line."
        >
          <Input
            id="admin.vouchers.ruleLabel"
            value={label}
            maxLength={80}
            onChange={(e) => setLabel(e.target.value)}
          />
        </FormField>
        {trigger === 'TICKET_CLAIMED' ? (
          <FormField
            label="Promotion label"
            description="Free text on the ticket e-mail and wherever the ticket QR is shown after claim."
          >
            <textarea
              id="admin.vouchers.rulePromotionLabel"
              value={promotionLabel}
              maxLength={200}
              rows={3}
              onChange={(e) => setPromotionLabel(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            />
          </FormField>
        ) : null}
        <p id="admin.vouchers.ruleTextsNote" className="text-sm text-gray-500">
          Edits here do not change vouchers already issued; they apply to vouchers minted from now on.
        </p>
        <FormField
          label="Active"
          description="When off, this rule stops minting. Already-minted vouchers keep their benefit."
        >
          <Switch
            id="admin.vouchers.ruleActive"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            label={active ? 'Active' : 'Inactive'}
          />
        </FormField>
      </FormSection>
      <FormSection title="Benefit" description="Snapshotted onto the voucher at mint time. There is no point price.">
        <FormField label="Benefit type" required>
          <Select
            id="admin.vouchers.ruleDiscountType"
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value as VoucherDiscountType)}
            options={[
              { value: 'PERCENTAGE', label: 'Percentage off' },
              { value: 'AMOUNT', label: 'Fixed euro amount' },
              { value: 'FREE_PRODUCT', label: 'Free product' },
            ]}
          />
        </FormField>
        {discountType === 'PERCENTAGE' ? (
          <FormField label="Percent" required>
            <Input
              id="admin.vouchers.rulePercent"
              type="number"
              min={1}
              max={100}
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(e.target.value)}
              required
            />
          </FormField>
        ) : null}
        {discountType === 'AMOUNT' ? (
          <FormField label="Amount (€)" required>
            <Input
              type="number"
              min={0.01}
              step="0.01"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(e.target.value)}
              required
            />
          </FormField>
        ) : null}
        {discountType === 'FREE_PRODUCT' ? (
          <FormField label="Product" required>
            <Select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              options={[
                { value: '', label: 'Select a product…' },
                ...(productsQuery.data?.products.nodes ?? []).map((p) => ({
                  value: p.id,
                  label: p.name,
                })),
              ]}
            />
          </FormField>
        ) : null}
      </FormSection>
    </FormLayout>
  );
}
